import { type ReactNode, createContext, useContext } from "react";
import { cx } from "../tv";

/** A slot after the page actions (the tour "?" menu on staff pages). The default is empty, so other shells keep their header. */
export const PageHeaderAccessory = createContext<ReactNode>(null);

/** Eyebrow (breadcrumb or context), title in Radio Canada Big, key figures, and one primary action (DESIGN-RULES 2). */
export function PageHeader({ eyebrow, title, badge, figures, people, actions, className }: {
  eyebrow?: ReactNode; title: string; badge?: ReactNode; figures?: ReactNode; people?: ReactNode; actions?: ReactNode; className?: string;
}) {
  const accessory = useContext(PageHeaderAccessory);
  return (
    <header className={cx("shrink-0 px-7 pb-3.5 pt-5.5 max-tablet:px-4 max-tablet:pt-4", className)}>
      {eyebrow && <div className="mb-2 flex min-w-0 items-center gap-1.5 text-sm text-fg-muted">{eyebrow}</div>}
      <div className="flex min-w-0 items-center gap-3 max-tablet:flex-wrap max-tablet:gap-y-2">
        {/* a title may wrap to two lines (DESIGN-RULES 6); on phones the badge and actions drop under a long one */}
        <h1 title={title} className="font-title min-w-0 line-clamp-2 text-balance text-4xl leading-tight max-tablet:text-3xl">{title}</h1>
        {badge}
        {(actions || accessory) && <div className="ms-auto flex shrink-0 items-center gap-2 max-tablet:-m-1 max-tablet:max-w-full max-tablet:overflow-x-auto max-tablet:p-1 max-tablet:overscroll-x-contain max-tablet:[scrollbar-width:none] max-tablet:[&::-webkit-scrollbar]:hidden">{actions}{accessory}</div>}
      </div>
      {(figures || people) && (
        <div className="mt-3.5 flex min-w-0 items-center gap-6.5 max-laptop:gap-4.5">
          <div className="flex min-w-0 items-center gap-6.5 overflow-hidden max-laptop:gap-4.5 max-tablet:flex-wrap max-tablet:gap-x-4 max-tablet:gap-y-1.5">{figures}</div>
          {people && <div className="ms-auto shrink-0 max-tablet:hidden">{people}</div>}
        </div>
      )}
    </header>
  );
}

/** One label and value pair; value in tabular UI figures, or a meter. */
export function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-base">
      <span className="text-fg-muted">{label}</span>
      <b className="tabular font-semibold text-fg">{children}</b>
    </div>
  );
}
